import React from 'react';

import { Box, Typography } from '@mui/material';
import { type BoxProps, styled } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import AuthError from './AuthError';
import EmailPasswordSignUpForm from './EmailPasswordSignUpForm';
import OtherJoinOptions from './OtherJoinOptions';
import SignUpContainer from './SignUpContainer';
import SignUpPageLoading from './SignUpPageLoading';
import { CLASS_LOGO_PATH } from '../../config/apiPaths';
import { TEAMS_EULA } from '../../constants/urls';
import { OrganizationType } from '../../hooks/useSchoolInfo';

const Container = styled(Box)`
    display: flex;
    flex-flow: column;
    height: 100vh;
    align-items: center;
`;

const SignUpBody = styled(Box)`
    max-width: 400px;
    margin: 6rem 4rem 4rem 4rem;
`;

const Title = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 2rem;
`;

const TitleTypography = styled(Typography)`
    text-align: center;
`;

type ImageWrapperProps = BoxProps & { imageUrl: string };

const ImageWrapper = styled(Box, {
    shouldForwardProp: (prop: string) => !['imageUrl'].includes(prop)
})<ImageWrapperProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 175px;
    height: 58px;
    margin: 0 auto 2rem;
    background: transparent url(${(props) => props.imageUrl}) no-repeat center;
    background-size: contain;
`;

const SignUpFooter = styled(Box)`
    margin-top: auto;
    width: 100%;
    display: flex;
    justify-content: flex-end;
    padding: 12px 16px;
    gap: 12px;
`;

const CreateUserPage = () => {
    const { isLoading, school, meetingData, isOriginMeeting, backToStateUrl } = useContainer(SignUpContainer);

    const showEulaLink = school?.type === OrganizationType.Teams;
    const { t } = useTranslation('sign-up');

    if (isLoading) {
        return <SignUpPageLoading />;
    }

    return (
        <Container>
            <SignUpBody>
                <ImageWrapper imageUrl={CLASS_LOGO_PATH} />

                <Title>
                    <TitleTypography variant="h1" gutterBottom>
                        {meetingData && t('sign-up-and-join-your-class')}
                        {!meetingData &&
                            (school?.name ? t('sign-up-to', { school: school?.name }) : t('sign-up-to-default'))}
                    </TitleTypography>
                </Title>

                <AuthError />

                <EmailPasswordSignUpForm />
            </SignUpBody>

            {isOriginMeeting && <OtherJoinOptions backToStateUrl={backToStateUrl} />}

            <SignUpFooter>
                {showEulaLink && (
                    <a href={TEAMS_EULA} target="_blank">
                        {t('eula')}
                    </a>
                )}
            </SignUpFooter>
        </Container>
    );
};

export default CreateUserPage;
